import { ChevronRight } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useAccountStore } from '../../../../components/Stores/accountStore';
import ButtonDesigned from '../../../../components/UI-Elements/Buttons/ButtonDesigned';
import { LinearGauge } from '../../../../components/UI-Elements/Charts/LinearGauge/LinearGauge';
import { IBalanceFlow, IBalanceFlowProps } from '../../../../sp-types/balanceFlowType';
import { IRoute } from '../../../../sp-types/routeType';
import { createEmptyRiskTolerance, getRiskTolerance } from '@utils/accountUtils';
import { getRouteTotalPrice } from '../../../../utils/routesUtils';
import { updateBalanceStepsStatusByIndexes } from '../../balanceUtils';
import { BalancingMethodMetricCardMap } from '../GeneralComponents/BalanceFlowMethodsMetricCards';
import {
  IBalancingMethod,
  balanceFlowToOptionalMethods,
  getBalanceExpectedDateRange,
  mapAdditionalArrowsByBalancingMethod,
} from '../Utils';
import { OverlayDisplayDiv } from '../../../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { CloseButton } from '../../../../components/UI-Elements/Buttons/CloseButton';

export const BalanceFlowMethodSelection: React.FC<IBalanceFlowProps> = ({
  balanceFlowData,
  updateBalanceFlowData,
  currentStepIndex,
  setCurrentStepIndex,
  setIsLoading,
  material,
  routesData,
}) => {
  const { formulaId, exposureBeforeBalance, isBalanceBlocked } = balanceFlowData;
  const { account } = useAccountStore();

  React.useEffect(() => {
    if (isBalanceBlocked) {
      setBalanceBlocked(true);
    }
  }, [account, isBalanceBlocked]);

  const [balanceBlocked, setBalanceBlocked] = React.useState<boolean>(!!isBalanceBlocked);

  const [balanceMethods, setBalanceMethods] = useState<IBalancingMethod[]>([]);
  const [selectedBalanceMethod, setSelectedBalanceMethod] = useState(
    balanceFlowData.selectedBalanceMethod?.balanceType || '',
  );

  useEffect(() => {
    if (account) {
      const balanceOpts = balanceFlowToOptionalMethods(
        exposureBeforeBalance,
        getRiskTolerance(formulaId, account) || createEmptyRiskTolerance(),
        selectedBalanceMethod,
        getExpectedPrice(routesData),
        getBalanceExpectedDateRange(balanceFlowData),
        setSelectedBalanceMethod,
        material.currentPrice,
      );

      //console.table(balanceOpts.reduce((acc, { infoTexts }) => [...acc, ...infoTexts], [] as any));

      setBalanceMethods(balanceOpts);
    }
  }, [account, exposureBeforeBalance, selectedBalanceMethod, routesData, material, formulaId]);

  async function updateFlowAndSteps() {
    setIsLoading(true);
    await updateBalanceFlowData({
      ...balanceFlowData,
      steps: updateBalanceStepsStatusByIndexes(balanceFlowData.steps, [
        { index: currentStepIndex, status: 'Approved' },
        { index: currentStepIndex + 1, status: 'Incomplete', doNotUpdateStatuses: ['Approved'] },
      ]),
      selectedBalanceMethod: findAndFormatBalanceMethod(
        balanceMethods,
        selectedBalanceMethod,
        balanceFlowData.exposureBeforeBalance,
      ),
    });
    setCurrentStepIndex((currIndex) => currIndex + 1);
    setIsLoading(false);
  }

  return (
    <div className="flex flex-1 flex-col  ">
      <h2 className="text-[1.125rem] leading-4 lg:block xl:block sm:hidden md:hidden mb-[2.5rem] ">Method selection</h2>
      <div className="border-t border-t-gray-100 pt-[1.5rem] space-y-[1.5rem] ">
        <h6 className="text-red m-0">Expected balance</h6>
        <p className="text-secondary m-0   ">Select the preferred options</p>
        <div className=" flex flex-col  flex-1 grow mx-auto pt-4 w-[70%] space-y-[3rem]">
          <LinearGauge
            isZoomExposure
            hightInRem={1.5}
            currentValue={exposureBeforeBalance}
            riskTolerance={getRiskTolerance(formulaId, account) || createEmptyRiskTolerance()}
            numberOfTicks={12}
            additionalArrows={mapAdditionalArrowsByBalancingMethod(balanceMethods, selectedBalanceMethod)}
          />
          <div className="flex flex-col space-y-[0.75rem]  grow justify-center ">
            <BalancingMethodMetricCardMap balancingMethodsData={balanceMethods} />
          </div>
        </div>
      </div>
      <div className="flex flex-row items-center mt-auto space-x-4">
        <ButtonDesigned
          backButton
          className="py-2"
          style="outline"
          onButtonClick={() => setCurrentStepIndex((currIndex) => currIndex - 1)}
        ></ButtonDesigned>
        <ButtonDesigned disableButton={!selectedBalanceMethod} onButtonClick={() => updateFlowAndSteps()}>
          Next <ChevronRight />
        </ButtonDesigned>
      </div>
      {balanceBlocked && (
        <OverlayDisplayDiv setDisplayOverlay={setBalanceBlocked} hideClose>
          <div className="bg-white shadow-sm flex flex-col justify-center items-center gap-4 py-4 px-2">
            <div className="flex flex-1 w-full justify-end items-end -m-4">
              <CloseButton onClick={() => setBalanceBlocked(false)} />
            </div>
            <p className="text-lg2">No availble routes</p>
            <div className="text-sm text-gray-700 mx-2">
              <p>We are unable to automatically recommend a suitable route at this time.</p>
              <p> For further assistance, please contact our customer service.</p>
              <p className="text-xs py-4"> (Error #23)</p>
            </div>
            <ButtonDesigned style="primary" className="py-2 px-4" onButtonClick={() => setBalanceBlocked(false)}>
              Close
            </ButtonDesigned>
          </div>
        </OverlayDisplayDiv>
      )}
    </div>
  );
};

function findAndFormatBalanceMethod(
  methods: IBalancingMethod[],
  type: string,
  currentExposure: number,
): IBalanceFlow['selectedBalanceMethod'] | undefined {
  const method = methods.find((method) => method.type === type);

  if (!method) {
    return undefined;
  }

  const dates = method.infoTexts.find((text) => text.title.toLowerCase() === 'due date')?.data as string;
  return {
    amountToBalance: currentExposure - method.exposure,
    dates: dates || '',
    balanceType: method.type,
  };
}

const getExpectedPrice = (route?: IRoute[]) => {
  return route && route.length > 0 ? getRouteTotalPrice(route[0].prices) : 0;
};
